import React, { useMemo } from 'react';
import classnames from 'classnames';
import shortid from 'shortid';
import { castArray, isNil, slice, noop, size, get, toNumber, isEmpty } from 'lodash';
import ReactTooltip from 'react-tooltip';

// local dependencies
import { textWithPatternMatch } from 'utils/textWithPatternMatch';
import { DATA_TYPES } from 'constants/common';
import { Tooltip } from 'modules/tooltip';
import { getDisplayValue } from 'resolvers/formatValue';
import { Icon } from 'modules/icon-bank';

const getIconForStepView = ( { value } ) => {
    const status = get( value, 'stepError', null );

    if( isNil( status ) || '' === status ) {
        return null;
    }

    if( 'CANCELLED' === status ) {
        return 'orderCancelled';
    } else {
        return 'statusError';
    }
};

/**
 * @desc Data cell view for single or mult-select data cells.
 * @example
 * import { simpleText as SimpleTextCellView } from './cellViews/simpleText.view';
 */
export const stepProgress = ( props ) => {

    // get proper display value
    const value = getDisplayValue( { type: props.columnType, value: props.value, options: props.options, config: props.config } ); // includes( DATA_TYPES.CURRENCY, props.columnType ) ? `$${ props.value }` : props.value;

    // extract display values and field values
    const displayValues = castArray( value.displayValue );

    // get visible display values based on `maxDisplayValues`
    const isValueTruncated = ! isNil( props.maxDisplayValues ) && ( 1 < size( displayValues ) );
    const visibleDisplayValues = isValueTruncated ? slice( displayValues, 0, props.maxDisplayValues ) : displayValues;
    
    // dynamic class name
    const widgetClassName = classnames( 'ui-table-cell-view-step-progress', props.className, {
        'ui-table-cell-view-step-progress--truncated': isValueTruncated,
        'ui-table-cell-view-step-progress--actionable': props.isActionable,
    } );

    // unique id
    const uniqId = useMemo( shortid.generate, [] );

    const iconId = getIconForStepView( { value } );

    return (
        <div className={ widgetClassName }>
            {/* display values */}
            <div className='ui-table-cell-view-step-progress__display-values' onClick={ props.onClick }>
                <div className='ui-table-cell-view-step-progress__display-values__steps'>

                    { /** steps */}

                    { 'CANCELLED' === get( value, 'stepError', '' ) && (
                        <div className="ui-table-cell-view-step-progress__display-values__steps__icon">
                            <Icon id={ iconId } />
                        </div>
                    ) }

                    <div className='ui-table-cell-view-step-progress__display-values__steps__items'>
                        { props.options.map( ( item, index ) => {
                            const currentStep = get( props, 'value.currentStep', 0 );

                            const stepClass = classnames( 'ui-table-cell-view-step-progress__display-values__steps__items__step',
                                {

                                    'ui-table-cell-view-step-progress__display-values__steps__items__step--error': '' !== currentStep && index === ( toNumber( currentStep ) - 1 ) && value.stepError,
                                    'ui-table-cell-view-step-progress__display-values__steps__items__step--highlight': '' !== currentStep && index <= ( toNumber( currentStep ) - 1 ),
                                    'ui-table-cell-view-step-progress__display-values__steps__items__step--active': '' !== currentStep && index === ( toNumber( currentStep ) - 1 ),
                                }
                            );

                            const cancelClassname = classnames( 'ui-table-cell-view-step-progress__display-values__steps__items__step', 'ui-table-cell-view-step-progress__display-values__steps__step--active' );
                            
                            return (
                                <span className={ 'CANCELLED' === value.stepError ? cancelClassname : stepClass } key={ 'step' + index }></span>
                            );
                        } ) }
                    </div>

                </div>

                {/* items */}
                <div className='ui-table-cell-view-step-progress__display-values__items'>

                    { ( ! isEmpty( get( value, 'orderStatus', '' ) ) && ! isEmpty( get( value, 'stepError' ) ) && 'CANCELLED' !== get( value, 'stepError', '' ) ) && (
                        <div className="ui-table-cell-view-step-progress__display-values__items__status">{ `${ get( value, 'orderStatus', '' ) } - ` } </div>
                    ) }

                    {
                        visibleDisplayValues.map( ( visibleDisplayValue, index ) => {

                            // const centerClass = classnames( '', {'ui-table-cell-view-step-progress__display-values__items__item__center': 'CANCELLED' === get( value, 'stepError', '' ) } );
                            return (
                                <Tooltip
                                    key={ index }
                                    text={ visibleDisplayValue }
                                    active={ ! isValueTruncated }
                                    activateOnOverflow={ true }
                                    render={ ( binding = {} ) => (
                                        <div
                                            { ...binding }
                                            className={ 'ui-table-cell-view-step-progress__display-values__items__item' }
                                            dangerouslySetInnerHTML={ { __html: props.isSearchable ? textWithPatternMatch( {
                                                value: visibleDisplayValue,
                                                pattern: props.searchQuery,
                                                wildcard: true,
                                                type: props.columnType,
                                            } ) : visibleDisplayValue } }
                                            { ...( isValueTruncated ? { 'data-tip': 'true', 'data-for': uniqId } : {} ) }
                                        />
                                    ) }
                                />
                            );
                        } )
                    }
                </div>

                {/* plus button */}
                {
                    isValueTruncated ? (
                        <div
                            className='ui-table-cell-view-step-progress__display-values__plus-button'
                            { ...( isValueTruncated ? { 'data-tip': 'true', 'data-for': uniqId } : {} ) }
                        >
                            +{ size( displayValues ) - 1 }
                        </div>
                    ) : null
                }
            </div>
            

            {/* tooltip */}
            {
                isValueTruncated ? (
                    <ReactTooltip
                        id={ uniqId }
                        aria-haspopup='true'
                        delayShow={ 300 }
                        className='ui-table-cell-view-simple-select__tooltip'
                        backgroundColor={ '#cdd7e1' }
                    >
                        {
                            displayValues.map( ( _valItem, _index ) => {
                                return (
                                    <p key={ _index } className='ui-table-cell-view-simple-select__tooltip__value'>
                                        { _valItem }
                                    </p>
                                );
                            } )
                        }
                    </ReactTooltip>
                ) : null
            }
        </div>
    );
};

// display name
stepProgress.displayName = 'table.cellViews.stepProgress';

// default values
stepProgress.defaultValues = {
    uniqId: shortid.generate(),
    value: [],
    maxDisplayValues: undefined,
    isSearchable: false,
    searchQuery: '',
    columnType: DATA_TYPES.TEXT,
    isActionable: false,
    onClick: noop,
};
